'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { cn } from '@/lib/utils';
import { bannersApi, type Banner } from '@/lib/api/admin-banners';

export interface PromoSlide {
  id: number;
  bgImageMobile: string;
  bgImageDesktop: string;
}

function mapBannersToSlides(banners: Banner[]): PromoSlide[] {
  return banners.map((b) => ({
    id: b.id,
    bgImageMobile: b.image_mobile,
    bgImageDesktop: b.image_desktop,
  }));
}

interface HeroBannerProps {
  className?: string;
}

/**
 * Hero-баннер на главной странице.
 *
 * Использует embla-carousel — это даёт:
 *   • Native touch drag с inertia: можно потянуть и увидеть половину следующего
 *   • Snap к слайдам после отпускания пальца
 *   • Бесшовный loop через loop:true
 *   • Autoplay через плагин с паузой во время взаимодействия (stopOnInteraction)
 *   • Стрелочки prev/next + дотс-индикаторы
 *
 * Заменяет старую opacity-fade реализацию (там не было drag-feedback,
 * слайды просто резко переключались).
 */
export function HeroBanner({ className }: HeroBannerProps) {
  const [slides, setSlides] = useState<PromoSlide[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Плагин autoplay — хранится в ref чтобы не пересоздавался при ре-рендере
  // и сохранял внутреннее состояние (counter, pause).
  const autoplayRef = useRef(
    Autoplay({
      delay: 6000,
      stopOnInteraction: false,    // не выключаемся навсегда после свайпа
      stopOnMouseEnter: true,      // пауза при наведении мыши на десктопе
    })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: false,             // snap к ближайшему слайду
      align: 'start',
      containScroll: 'trimSnaps',  // убираем пустоту по краям
    },
    [autoplayRef.current]
  );

  // Загрузка баннеров с API
  useEffect(() => {
    bannersApi.getActive()
      .then((banners) => {
        if (banners.length > 0) {
          setSlides(mapBannersToSlides(banners));
        }
      })
      .catch(() => {
        // API недоступен — останется скелетон
      });
  }, []);

  // Слежение за активным слайдом для дотс-индикаторов
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Embla нужно переинициализировать когда слайды загрузились —
  // он считает слайды в момент первой инициализации.
  useEffect(() => {
    if (emblaApi && slides) {
      emblaApi.reInit();
    }
  }, [emblaApi, slides]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <div className={cn('relative w-full', className)}>
      {/* Embla viewport — обрезает overflow, ловит touch/mouse drag */}
      <div
        className={cn(
          'relative w-full overflow-hidden',
          'h-[520px] sm:h-[540px] md:h-[580px] md:max-h-[580px]'
        )}
        ref={emblaRef}
      >
        {/* Скелетон пока баннеры грузятся */}
        {!slides && (
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--primary)] to-[var(--violet-700)] animate-pulse" />
        )}

        {/* Embla container — flex с translateX, embla сам управляет transform */}
        {slides && (
          <div className="flex h-full touch-pan-y">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className="relative flex-[0_0_100%] min-w-0 h-full"
              >
                <picture>
                  <source media="(min-width: 768px)" srcSet={slide.bgImageDesktop} />
                  <img
                    src={slide.bgImageMobile}
                    alt=""
                    className="w-full h-full object-cover object-center select-none"
                    draggable={false}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                </picture>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Дотс-индикаторы внизу — показываем только если слайдов больше 1 */}
      {slides && slides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Перейти к слайду ${i + 1}`}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === selectedIndex
                  ? 'w-6 bg-white shadow-sm'
                  : 'w-1.5 bg-white/50 hover:bg-white/75'
              )}
            />
          ))}
        </div>
      )}

      {/* Стрелочки prev/next — только на десктопе (md+), на мобильном свайп */}
      {slides && slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Предыдущий баннер"
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur items-center justify-center text-white transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Следующий баннер"
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur items-center justify-center text-white transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
